/**
 * The editor's text forms of a mention — pure, so they can be tested without
 * TipTap. `MentionEditor` uses them for the draft's marker string
 * (`getProcessedText`), the clipboard (`renderText` and the slice
 * serialiser) and the Enter-vs-menu decision.
 */
import { encodeLabel, isSkillMentionType, serializeFamilyType, serializeParams, type MentionParameter } from "./mentionUtils";
import { requireDocumentContextElementId } from "./elementId";

function paramsOf(attrs: Record<string, unknown>): MentionParameter[] {
  return Array.isArray(attrs["selectedParams"]) ? (attrs["selectedParams"] as MentionParameter[]) : [];
}

/** The human text of a chip — `@label`, or `@label: p1|p2` with parameters. */
export function mentionNodeText(attrs: Record<string, unknown>): string {
  const label = String(attrs["label"] ?? "");
  const params = paramsOf(attrs);
  if (params.length === 0) return `@${label}`;
  return `@${label}: ${params.map((p) => p.name).join("|")}`;
}

/**
 * One mention node as its wire marker — the grammar in `mentionUtils.ts`:
 * plain `[[mention:type:id:label]]`, or the full form with the params and
 * family halves when either is present (always emitted as a pair).
 */
export function mentionNodeMarker(attrs: Record<string, unknown>): string {
  const typeName = String(attrs["type"] ?? "");
  const label = String(attrs["label"] ?? "");
  const id = isSkillMentionType(typeName)
    ? String(attrs["id"] ?? "")
    : requireDocumentContextElementId(typeName, attrs["id"]);
  const params = paramsOf(attrs);
  const familyType = serializeFamilyType(
    typeof attrs["familyName"] === "string" ? attrs["familyName"] : null,
    typeof attrs["typeName"] === "string" ? attrs["typeName"] : null,
  );
  const hasParams = params.length > 0;
  const tail = hasParams || familyType.length > 0 ? `:${hasParams ? serializeParams(params) : ""}:${familyType}` : "";
  const safeId = isSkillMentionType(typeName) ? encodeLabel(id) : id;
  return `[[mention:${typeName}:${safeId}:${encodeLabel(label)}${tail}]]`;
}

/** The shape of a ProseMirror node this walker needs. */
export interface TextNodeLike {
  typeName: string;
  isText: boolean;
  isBlock: boolean;
  text?: string | null;
  attrs: Record<string, unknown>;
}

/**
 * A document as marker text: mentions as markers, text as is, a newline
 * between blocks — the old `getProcessedText`, over a flat visit order.
 */
export function documentToMarkerText(nodes: readonly TextNodeLike[]): string {
  let text = "";
  for (const node of nodes) {
    if (node.typeName === "mention") text += mentionNodeMarker(node.attrs);
    else if (node.isText) text += node.text ?? "";
    else if (node.isBlock && text.length > 0) text += "\n";
  }
  return text;
}

/**
 * Is an `@` menu plausibly open at the caret — an `@` before it with no
 * whitespace after? If so Enter belongs to the menu, not to Send. The
 * reference's own heuristic; the suggestion plugin's real state is not
 * exposed to the keymap.
 */
export function suggestionMayBeOpen(textBeforeCaret: string): boolean {
  const at = textBeforeCaret.lastIndexOf("@");
  if (at === -1) return false;
  const after = textBeforeCaret.slice(at + 1);
  return !after.includes(" ") && !after.includes("\n");
}
